import { useMemo } from 'react';
import { PLAYER_GROUPS } from '@leopart/calc';
import { DistributionSlice } from '../../../shared/charts/chart.types';
import { ITrainingMixPlayerDensity } from '../../../models/allenamenti';
import { buildFlowBars } from './analysis.constants';

/** Riga di dettaglio per un gruppo dimensionale: dati + barre modalità pronte per il render. */
export interface IPlayerGroupDetail {
  id: string;
  label: string;
  color: string;
  minutes: number;
  blocks: number;
  pct: number | null;
  avgAreaM2: number | null;
  flowBars: DistributionSlice[];
}

/**
 * Logica derivata del pannello «Dimensione dei gruppi» (F2): mappa i dati già
 * pronti dal server (`data.playerDensity`) sulle costanti @leopart/calc
 * (PLAYER_GROUPS/FLOWS, label e colori), senza ricalcolare nessuna soglia qui.
 */
export const usePlayerGroupPanel = (density: ITrainingMixPlayerDensity | null) => {
  const groupBars: DistributionSlice[] = useMemo(() => {
    if (!density) return [];
    return PLAYER_GROUPS.map((group) => {
      const row = density.byGroup.find((g) => g.group === group.id);
      return {
        id: group.id,
        label: group.labelIt,
        value: row?.minutes ?? 0,
        percent: row?.pct ?? null,
        color: group.color,
      };
    });
  }, [density]);

  const groupDetails: IPlayerGroupDetail[] = useMemo(() => {
    if (!density) return [];
    return PLAYER_GROUPS.map((group) => {
      const row = density.byGroup.find((g) => g.group === group.id);
      return {
        id: group.id,
        label: group.labelIt,
        color: group.color,
        minutes: row?.minutes ?? 0,
        blocks: row?.blocks ?? 0,
        pct: row?.pct ?? null,
        avgAreaM2: row?.avgAreaM2 ?? null,
        // Senza fattore (×0.65): nella mini-card stretta l'etichetta andrebbe a capo.
        flowBars: buildFlowBars(row?.byFlow ?? [], false),
      };
    });
  }, [density]);

  const totalFlowBars: DistributionSlice[] = useMemo(
    () => (density ? buildFlowBars(density.byFlowTotal) : []),
    [density],
  );

  return { groupBars, groupDetails, totalFlowBars };
};
